"use client"

import { useState, useSyncExternalStore } from "react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
    OPEN_SETTINGS_EVENT,
    getConsentSnapshot,
    getServerConsentSnapshot,
    mapsAllowed,
    subscribeConsent,
} from "@/lib/analytics"
import { buildMapQuery, mapEmbedUrl, mapLinkUrl, type MapAddress } from "@/lib/map-query"

const FRAME_SIZE = "h-[320px] w-full sm:h-[420px]"

const linkClass =
    "underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"

/** Opens the cookie box (mounted in the layout), the only place the choice is made. */
function CookieSettingsButton() {
    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT))}
        >
            Cookie settings
        </Button>
    )
}

/** The Google map frame, with a skeleton until it has loaded. Mounted only after consent. */
function MapFrame({ src, title }: { src: string; title: string }) {
    const [loaded, setLoaded] = useState(false)
    return (
        <div className={`relative ${FRAME_SIZE}`}>
            {!loaded ? <Skeleton className="absolute inset-0 h-full w-full" /> : null}
            <iframe
                src={src}
                title={title}
                loading="lazy"
                allowFullScreen
                onLoad={() => setLoaded(true)}
                className="absolute inset-0 h-full w-full border-0"
            />
        </div>
    )
}

/**
 * The listing's location. The Google map loads only for visitors who accepted the cookie
 * box (and only when a Maps key is set), automatically and with no reload when the choice
 * changes, here or in another tab. Everyone else sees the address and a plain link.
 */
export default function ListingMap({ address, city, post, province, embedKey }: MapAddress & { embedKey: string }) {
    // "pending" on the server and during hydration: storage is never read there.
    const consent = useSyncExternalStore(subscribeConsent, getConsentSnapshot, getServerConsentSnapshot)
    const query = buildMapQuery({ address, city, post, province })
    const street = (address ?? "").trim()
    if (!query) return null

    const link = (
        <a href={mapLinkUrl(query)} target="_blank" rel="noopener noreferrer" className={linkClass}>
            Open in Google Maps
            <span className="sr-only"> (opens in a new tab)</span>
        </a>
    )

    let body
    if (consent === "pending") {
        body = <Skeleton className={FRAME_SIZE} aria-hidden="true" />
    } else if (embedKey && mapsAllowed(consent)) {
        body = (
            <div className="flex flex-col gap-2">
                <MapFrame src={mapEmbedUrl(embedKey, query)} title={`Map of ${street}`} />
                <div>
                    <CookieSettingsButton />
                </div>
            </div>
        )
    } else if (embedKey) {
        body = (
            <div className="flex flex-col items-center gap-3 text-center text-sm">
                <p className="font-semibold">{query}</p>
                <p>The map is shown to visitors who accept cookies.</p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                    <CookieSettingsButton />
                    {link}
                </div>
            </div>
        )
    } else {
        body = (
            <div className="flex flex-col items-center gap-3 text-center text-sm">
                <p className="font-semibold">{query}</p>
                {link}
            </div>
        )
    }

    return (
        <section aria-label="Location" aria-busy={consent === "pending"} className="pb-5">
            {body}
        </section>
    )
}
